import { useEffect, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { RTU } from "../../types/RTU";
import { SkeletonRegion, SkeletonTable } from "../common/Skeleton/Skeleton";

const displayRtuName = (rtu: RTU): string => {
  const trimmed = rtu.name?.trim();
  return trimmed ? trimmed : `RTU ${rtu.id}`;
};

export const LowDiskStorageDevices = ({
  rtus,
  thresholdPercent,
}: {
  rtus: RTU[];
  thresholdPercent: number;
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [lowStorageDevices, setLowStorageDevices] = useState<RTU[]>([]);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1>(1);

  useEffect(() => {
    setLowStorageDevices(rtus.filter((rtu) => rtu.diskUsage >= thresholdPercent));
    setLoading(false);
  }, [rtus, thresholdPercent]);

  const diskUsageBodyTemplate = (rowData: RTU) => `${rowData.diskUsage.toFixed(2)}%`;

  const lastUpdatedTemplate = (rowData: RTU) => {
    const d = new Date(rowData.lastData);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };

  const renderEmptyMessage = () => {
    if (loading) {
      return (
        <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
          <SkeletonTable columns={[1.4, 1, 1]} rows={3} showHeader={false} />
        </SkeletonRegion>
      );
    }

    if (lowStorageDevices.length === 0) {
      return <div>{t("dashboard.lowDisk.empty")}</div>;
    }

    return null;
  };

  const navigate = useNavigate();
  const onRowSelect = (event: any) => {
    if (event.originalEvent.target.role !== "checkbox") {
      const rtuId = event.data.id;
      const rtu = rtus.find((item) => item.id === rtuId);
      navigate(`/rtupage/${rtuId}`, { state: { rtu } });
    }
  };

  const customSort = (event: { sortField: string; sortOrder: 1 | 0 | -1 }) => {
    const { sortField: nextField, sortOrder: nextOrder } = event;
    setSortField(nextField);
    setSortOrder(nextOrder);

    if (nextField === "lastUpdated") {
      setLowStorageDevices(
        [...lowStorageDevices].sort((a, b) => {
          const dateA = new Date(a.lastData);
          const dateB = new Date(b.lastData);
          return nextOrder * (dateA.getTime() - dateB.getTime());
        })
      );
      return;
    }

    setLowStorageDevices(
      [...lowStorageDevices].sort((a, b) => {
        if (nextField === "name") {
          const labelA = displayRtuName(a);
          const labelB = displayRtuName(b);
          if (labelA < labelB) return -1 * nextOrder;
          if (labelA > labelB) return 1 * nextOrder;
          return 0;
        }
        const valueA = a[nextField as keyof RTU] ?? "";
        const valueB = b[nextField as keyof RTU] ?? "";
        if (valueA < valueB) return -1 * nextOrder;
        if (valueA > valueB) return 1 * nextOrder;
        return 0;
      })
    );
  };

  return (
    <DataTable
      value={lowStorageDevices}
      tableStyle={{ width: "100%", minWidth: "max-content" }}
      showGridlines
      emptyMessage={renderEmptyMessage()}
      onRowSelect={onRowSelect}
      selectionMode="single"
      removableSort
      sortField={sortField}
      sortOrder={sortOrder}
      onSort={(e: any) => customSort(e)}
    >
      <Column
        field="name"
        header={t("dashboard.tables.columns.name")}
        sortable
        style={{ color: "#1c80cf", fontWeight: "bold", whiteSpace: "nowrap" }}
        body={(rowData: RTU) => displayRtuName(rowData)}
      />
      <Column
        field="diskUsage"
        header={t("dashboard.tables.columns.storageUsed")}
        sortable
        style={{ whiteSpace: "nowrap" }}
        body={diskUsageBodyTemplate}
        bodyStyle={{ textAlign: "center" }}
      />
      <Column
        field="lastUpdated"
        header={t("dashboard.tables.columns.reported")}
        sortable
        body={lastUpdatedTemplate}
        style={{ whiteSpace: "nowrap" }}
        bodyStyle={{ textAlign: "center" }}
      />
    </DataTable>
  );
};
